import last from 'lodash/last';
import debounce from 'lodash/debounce';
import { DateTime } from 'luxon';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

import {
  AdminGetJobsFilterInput, AdminJobOrderByOption, DateRangeFilter, JobSpeciality, JobStatus, JobType, Order, PageInfo
} from '@clipr/lib';

import { ApiResult, ApiVariables } from '../api/apiSchema';
import { input, InputState } from '../components';
import { DateInputState, dateInput } from '../components/input/dateInput/dateInputState';
import { SortFieldValue } from '../components/input/sortFieldBtn';
import { assertNotNull, AsyncResult, Maybe, Nullable } from '../core';
import { Timer } from '../core/timer';
import { notifyError } from '../services/notifications';
import { isAgencyAccessRefused } from '../api/apiErrorUtils';
import { BindingProps, StoreNode } from '../store';
import { Store } from '../store/store';
import { SyncStatus } from '../store/syncSchema';
import { JobModel } from './job';
import { JobCatalogItem } from './jobCatalogItem';
import { JobLevel } from './job/jobFeatures';

type TrainerJobFilterField = 'videoType' | 'status' | 'level' | 'speciality';
type TrainerJobFilterValue = JobType | JobStatus | JobLevel | JobSpeciality;
import { JobsPosterSize } from './jobManager';
import { JobFilter } from '.';

const PROCESSING_JOBS_REFRESH_INTERVAL = 4000;

type Props = BindingProps<{
  pageSize?: number
}> & {
  /** 
   * Local filter to apply to all Job entities. 
   * Currently it's only being used to detect the transient entities.
   */
  jobFilter?: JobFilter;
};

export class TrainerJobCatalogSource
  extends StoreNode {

  readonly nodeType: 'TrainerJobCatalogSource' = 'TrainerJobCatalogSource';
  readonly dateInput: DateInputState;

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'getFetchArgs' | 'fetchRaw'>(this, {
      pageSize: computed,
      jobFilter: computed,
      syncStatus: observable,
      videoTypeFilter: observable,
      levelFilter: observable,
      statusFilter: observable,
      specialityFilter: observable,
      createdByIdFilter: observable,
      beforeDateFilter: observable,
      afterDateFilter: observable,
      searchFilter: observable,
      sortField: observable,
      sortOrder: observable,
      pageInfo: observable,
      lastItemCursor: computed,
      itemEntities: computed,
      transientEntities: computed,
      entities: computed,
      processingJobIds: computed,
      isEndOfList: computed,
      setSortFieldValue: action,
      setFilterFieldValue: action,
      fetch: action,
      fetchMore: action,
      getFetchArgs: action,
      fetchRaw: action,
      handleSearchInputChange: action,
      handleDateFilterChange: action,
      reset: action,
      _injectJobs: action,
    });

    this.searchBarModel = input(this, {
      name: 'searchInput',
      onChange: (e) => this.handleSearchInputChange(e),
    });

    this.dateInput = dateInput(this, {
      placeholder: ['dd/mm/yyyy', 'dd/mm/yyyy'],
      format: ['DD/MM/YYYY', 'DDMMYYYY'],
      rangeOptions: [{
        name: 'Today',
        getRange: () => {
          return [dayjs(), dayjs()];
        }
      },
      {
        name: 'Yesterday',
        getRange: () => {
          return [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')];
        }
      },
      {
        name: 'Last 30 Days',
        getRange: () => {
          return [dayjs().subtract(31, 'day'), dayjs()];
        }
      },
      {
        name: 'This Month',
        getRange: () => {
          return [dayjs().startOf('month'), dayjs().endOf('month')]
        }
      }]
    });
  }

  // #region Resolved props
  // -------
  get pageSize(): number {
    return this.resolvedProps.pageSize;
  }

  get jobFilter(): JobFilter | null {
    return this.getProp('jobFilter');
  }
  // #endregion

  readonly items = observable.array<JobCatalogItem>();
  readonly searchBarModel: InputState;
  private readonly processingJobsRefreshTimer = new Timer({
    timeout: PROCESSING_JOBS_REFRESH_INTERVAL,
    callback: () => this.refreshProcessingJobs(),
  });
  private processingJobsAbortController: AbortController | null = null;
  private isRefreshingProcessingJobs = false;
  /** Bumped by every fresh fetch and reset so slower, older responses can't overwrite the list. */
  private fetchGeneration = 0;

  syncStatus: SyncStatus = 'idle';

  videoTypeFilter: JobType | null = null;
  levelFilter: JobLevel | null = null;
  statusFilter: JobStatus | null = null;
  specialityFilter: JobSpeciality | null = null;
  createdByIdFilter: string | null = null;
  beforeDateFilter: Date | null = null;
  afterDateFilter: Date | null = null;
  searchFilter: string | null = null;

  sortField: AdminJobOrderByOption | null = 'createdAt';
  sortOrder: Order | null = 'desc';

  pageInfo: PageInfo | null = null;

  get lastItemCursor(): string | null {
    return last(this.items)?.cursor || null;
  }

  get itemEntities(): JobModel[] {
    return this.items.map(item => item.job);
  }

  get transientEntities(): JobModel[] {
    const filter = this.jobFilter;
    if (typeof filter !== 'function')
      return [];

    return this.store.jobManager.transientJobs.filter(job => filter(job));
  }

  get entities(): JobModel[] {
    return [
      ...this.transientEntities,
      ...this.itemEntities
    ];
  }

  get processingJobIds(): string[] {
    return this.itemEntities
      .filter(job => job.isMediaProcessing || job.isInProgress)
      .map(job => job.id);
  }

  get isEndOfList(): boolean {
    return this.pageInfo ? !this.pageInfo.hasNextPage : false;
  }


  getSortFieldValue(field: AdminJobOrderByOption) {
    if (this.sortField !== field)
      return 'none';
    return this.sortOrder!;
  }

  setSortFieldValue(field: AdminJobOrderByOption, val?: SortFieldValue) {
    if (!val || val === 'none') {
      this.sortField = null;
      this.sortOrder = null;
    } else {
      this.sortField = field;
      this.sortOrder = val;
    }
    this.fetchDebounced();
  }


  getFilterFieldValue(field: TrainerJobFilterField): Nullable<TrainerJobFilterValue> {
    switch (field) {
      case 'videoType':
        return this.videoTypeFilter;
      case 'status':
        return this.statusFilter;
      case 'level':
        return this.levelFilter;
      case 'speciality':
        return this.specialityFilter;
    }
    return null;
  }

  setFilterFieldValue(field: TrainerJobFilterField, val?: Maybe<TrainerJobFilterValue>) {

    switch (field) {
      case 'videoType':
        this.videoTypeFilter = (val as JobType) || null;
        break;
      case 'status':
        this.statusFilter = (val as JobStatus) || null;
        break;
      case 'level':
        this.levelFilter = (val as JobLevel) || null;
        break;
      case 'speciality':
        this.specialityFilter = (val as JobSpeciality) || null;
        break;

    }
    this.fetchDebounced();
  }

  fetchDebounced = debounce(() => this.fetch(), 500);

  /** Makes a fresh fetch call, clearing any previous data and pagination state. */
  async fetch()
    : AsyncResult<JobCatalogItem[]> {

    this.syncStatus = 'fetching';
    const generation = ++this.fetchGeneration;

    const [jobsRes, err] = await this.fetchRaw();
    // A newer filter/sort fetch owns the list now (QA BUG-040: "All" landing after "LiveStreaming").
    if (generation !== this.fetchGeneration)
      return [[]];

    if (err)
      return [null, err];
    assertNotNull(jobsRes);

    const retItems: JobCatalogItem[] = [];
    runInAction(() => {
      this.items.clear();

      const { adminGetJobs } = jobsRes;
      this.pageInfo = adminGetJobs.pageInfo;

      adminGetJobs.edges.forEach((edge, i) => {

        const item = new JobCatalogItem(this.store, {
          jobId: edge.node.id,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);
      });
    });

    this.syncProcessingJobsRefresh();
    return [retItems];
  }

  async fetchMore()
    : AsyncResult<JobCatalogItem[]> {

    this.syncStatus = 'fetchingMore';
    const generation = this.fetchGeneration;

    const [jobsRes, err] = await this.fetchRaw(true);
    if (generation !== this.fetchGeneration)
      return [[]];
    if (err)
      return [null, err];
    assertNotNull(jobsRes);

    const retItems: JobCatalogItem[] = [];
    runInAction(() => {
      const { adminGetJobs } = jobsRes;
      this.pageInfo = adminGetJobs.pageInfo;

      adminGetJobs.edges.forEach((edge, i) => {

        const item = new JobCatalogItem(this.store, {
          jobId: edge.node.id,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);
      });
    });

    this.syncProcessingJobsRefresh();
    return [retItems];
  }

  private getFetchArgs(more: boolean = false) {
    const args: ApiVariables<'adminGetJobs'> = {
      posterSize: [JobsPosterSize]
    };
    const filter: AdminGetJobsFilterInput = {};

    if (this.sortField && this.sortOrder) {
      args.sort = {
        field: this.sortField,
        order: this.sortOrder
      }
    }

    if (this.createdByIdFilter) {
      filter.createdById = this.createdByIdFilter;
    }

    if (this.statusFilter) {
      filter.status = {
        eq: this.statusFilter
      }
    }

    if (this.specialityFilter) {
      filter.speciality = this.specialityFilter as string;
    }

    if (this.videoTypeFilter) {
      filter.videoType = this.videoTypeFilter;
    }

    if (this.levelFilter) {
      filter.feature = {
        eq: this.levelFilter
      }
    }

    const createdAt: Maybe<DateRangeFilter> = {};
    if (this.beforeDateFilter) {
      // send the before date until almost midnight, otherwise it will be useless; endOf, not
      // plus(23:59:59.999), so a 25-hour DST day keeps its last hour
      createdAt.lt = DateTime
        .fromJSDate(this.beforeDateFilter)
        .endOf('day')
        .toUTC();
    }
    if (this.afterDateFilter) {
      createdAt.gte = DateTime
        .fromJSDate(this.afterDateFilter)
        .toUTC();
    }

    if (Object.keys(createdAt).length > 0)
      filter.createdAt = createdAt;

    if (this.searchFilter) {
      filter._search = this.searchFilter || undefined;
    }

    if (Object.keys(filter).length > 0)
      args.filter = filter;

    args.first = this.pageSize;

    if (more && this.lastItemCursor)
      args.after = this.lastItemCursor;

    return args;
  }

  private async fetchRaw(more: boolean = false): ApiResult<'adminGetJobs'> {

    const args: ApiVariables<'adminGetJobs'> = this.getFetchArgs(more);
    const { jobManager } = this.store;

    const [jobsRes, err] = await jobManager.apiFetchTrainerJobs(args, {
      rawResponse: true
    });

    if (err) {
      runInAction(() => {
        this.syncStatus = 'error';
        // A refused agency request renders as an empty state; the page refreshes access.
        if (!isAgencyAccessRefused(err))
          notifyError(this, `Could not fetch videos because of an error.`);
      });
      return [null, err];
    }

    runInAction(() => {
      this.syncStatus = 'idle';
    });

    assertNotNull(jobsRes);
    return [jobsRes];
  }


  handleSearchInputChange(evt: InputState) {
    this.searchFilter = evt.value || null;
    this.fetchDebounced();
  }

  handleDateFilterChange() {
    const dateInputValue = this.dateInput.value as [Dayjs, Dayjs];
    if (dateInputValue) {
      // Local days: the dashboard shows upload times in local time, and .utc() first shifted typed dates back a day east of UTC.
      const formattedStartDate = dateInputValue[0].startOf('day');
      const formattedEndDate = dateInputValue[1].startOf('day');

      this.beforeDateFilter = formattedEndDate.toDate();
      this.afterDateFilter = formattedStartDate.toDate();
    } else {
      this.beforeDateFilter = null;
      this.afterDateFilter = null;
    }

    this.fetchDebounced();
  }

  reset() {
    this.fetchGeneration++;
    this.cancelProcessingJobsRefresh();
    this.items.clear();
    this.pageInfo = null;
    this.searchFilter = null;

    this.statusFilter = null;
    this.specialityFilter = null;
    this.levelFilter = null;

    this.videoTypeFilter = null;
    this.createdByIdFilter = null;

    this.beforeDateFilter = null;
    this.afterDateFilter = null;

    this.sortField = 'createdAt';
    this.sortOrder = 'desc';
    this.searchBarModel.clear();
  }

  private syncProcessingJobsRefresh() {
    if (this.processingJobIds.length)
      this.processingJobsRefreshTimer.start();
    else
      this.cancelProcessingJobsRefresh();
  }

  private cancelProcessingJobsRefresh() {
    this.processingJobsRefreshTimer.cancel();
    this.processingJobsAbortController?.abort();
    this.processingJobsAbortController = null;
    this.isRefreshingProcessingJobs = false;
  }

  private async refreshProcessingJobs() {
    if (this.isRefreshingProcessingJobs) {
      this.syncProcessingJobsRefresh();
      return;
    }

    const jobIds = this.processingJobIds;
    if (!jobIds.length) {
      this.cancelProcessingJobsRefresh();
      return;
    }

    const abortController = new AbortController();
    this.processingJobsAbortController = abortController;
    this.isRefreshingProcessingJobs = true;

    try {
      await Promise.all(jobIds.map(jobId => this.store.jobManager.apiFetchJob(
        jobId,
        false,
        {
          signal: abortController.signal,
          suppressRedirectWhenOffline: true,
        },
      )));
    } catch {
      // Polling is best-effort; keep the catalog usable and try again on the next tick.
    } finally {
      this.isRefreshingProcessingJobs = false;
      if (this.processingJobsAbortController === abortController)
        this.processingJobsAbortController = null;

      if (!abortController.signal.aborted)
        this.syncProcessingJobsRefresh();
    }
  }

  // TEMP just for testing
  _injectJobs(jobs: JobModel[]) {

    runInAction(() => {
      this.pageInfo = null;

      jobs.forEach((job, i) => {

        this.items.push(new JobCatalogItem(this.store, {
          jobId: job.id,
          cursor: 'TEST_CURSOR_' + job.id,
          index: i
        }));
      });
    });
  }
}
